import { CheckCircle, XCircle } from '@phosphor-icons/react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { client } from '../api/client'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useAuthStore } from '../stores/auth'

export function VerifyEmailPage() {
  const { t } = useTranslation('auth')
  const { token } = useParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const { updateParent, parent, isAuthenticated } = useAuthStore()
  const verificationAttempted = useRef(false)

  useEffect(() => {
    // If user is already authenticated and email is verified, redirect to homepage
    if (isAuthenticated && parent?.is_email_verified) {
      navigate('/', { replace: true })
      return
    }

    const verifyEmail = async () => {
      // Prevent multiple verification attempts
      if (verificationAttempted.current) return
      verificationAttempted.current = true

      if (!token) {
        setErrorMessage(t('verification.error'))
        setStatus('error')
        return
      }

      try {
        const response = await client.post(`/auth/verify-email/${token}/`)
        
        if (response.data.success) {
          setStatus('success')
          // Update parent data with the response data
          updateParent(response.data.parent)
          
          // Always redirect to home page after successful verification
          setTimeout(() => navigate('/'), 3000)
        } else {
          // Verification failed with message
          
          // Map backend error messages to translation keys
          let translationKey = 'verification.error'
          if (response.data.message.includes('already been used')) {
            translationKey = 'verification.alreadyUsed'
          } else if (response.data.message.includes('expired')) {
            translationKey = 'verification.expired'
          } else if (response.data.message.includes('Invalid verification link')) {
            translationKey = 'verification.invalid'
          }
          
          setErrorMessage(t(translationKey))
          setStatus('error')
          
          // If the token is invalid, redirect to verification pending page
          if (response.data.message.includes('no longer valid') || 
              response.data.message.includes('Invalid verification link')) {
            setTimeout(() => navigate('/verification-pending'), 3000)
          }
        }
      } catch (error: any) {
        // Verification error handling
        
        // Map backend error messages to translation keys
        let translationKey = 'verification.error'
        const errorMessage = error.response?.data?.message || ''
        
        if (errorMessage.includes('already been used')) {
          translationKey = 'verification.alreadyUsed'
        } else if (errorMessage.includes('expired')) {
          translationKey = 'verification.expired'
        } else if (errorMessage.includes('Invalid verification link')) {
          translationKey = 'verification.invalid'
        }
        
        setErrorMessage(t(translationKey))
        setStatus('error')
      }
    }

    verifyEmail()
  }, [token, navigate, t, updateParent, isAuthenticated, parent])

  return (
    <>
      {/* Fixed Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="auth-container" style={{ minHeight: '100vh' }}>
        <div className="max-w-md w-full">
          <div className="card-neobrutalism">
            {status === 'verifying' && (
              <div className="text-center space-y-4">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-black border-t-transparent mx-auto"></div>
                <h2 className="text-2xl font-black text-neobrutalism-text">
                  {t('verification.verifying')}
                </h2>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center space-y-4">
                <div className="bg-green-50 p-6 rounded-lg shadow-sm">
                  <div className="flex flex-col items-center">
                    <CheckCircle size={64} weight="fill" className="text-green-600 mb-4" />
                    <h2 className="text-2xl font-bold text-green-800">
                      {t('verification.success')}
                    </h2>
                  </div>
                </div>
                <p className="text-lg font-medium text-neobrutalism-text">
                  {t('verification.redirectingHome')}
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center space-y-4">
                <div className="bg-red-50 p-6 rounded-lg shadow-sm">
                  <div className="flex flex-col items-center">
                    <XCircle size={64} weight="fill" className="text-red-600 mb-4" />
                    <h2 className="text-xl font-bold text-red-800">
                      {errorMessage}
                    </h2>
                  </div>
                </div>
                <button 
                  onClick={() => navigate('/login')} 
                  className="btn-neobrutalism bg-neobrutalism-blue text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
                >
                  {t('register.loginLink')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
} 